import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon, WidgetSkeleton } from '..';
import type { IconType } from '../Icon';
import { Statistics } from '../../services';
import type { SupportIndicators } from '../../services/Statistics';

const UNANSWERED_HOURS = 48;
const TRIAGE_LINK = '/changes?status=pendingTriage';
const TRIAGE_PAGE = '/triage';

type KpiTile = {
    key: string;
    icon: IconType;
    href: string;
    count: number;
    detail?: string | null;
};

function formatHours(
    value: number | null | undefined,
    t: (key: string, options?: Record<string, string | number>) => string,
): string | null {
    if (value == null || Number.isNaN(value))
        return null;
    if (value < 1)
        return t('dashboard.support_age_minutes', { count: Math.round(value * 60) });
    if (value < 48)
        return t('dashboard.support_age_hours', { count: value.toFixed(1) });
    return t('dashboard.support_age_days', { count: (value / 24).toFixed(1) });
}

function SupportIndicatorsWidget() {
    const { t } = useTranslation();
    const [support, setSupport] = useState<SupportIndicators | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        Statistics.getSupportIndicators({ unanswered_hours: UNANSWERED_HOURS })
            .then((data) => {
                if (!cancelled)
                    setSupport(data.support);
            })
            .catch(() => {
                if (!cancelled)
                    setSupport(null);
            })
            .finally(() => {
                if (!cancelled)
                    setLoading(false);
            });
        return () => { cancelled = true; };
    }, []);

    if (loading)
        return <WidgetSkeleton rows={1} />;

    if (!support)
        return null;

    const tiles: KpiTile[] = [
        {
            key: 'pending_triage_age',
            icon: 'task',
            href: TRIAGE_PAGE,
            count: support.pendingTriage.count,
            detail: support.pendingTriage.count > 0
                ? t('dashboard.support_age_avg', {
                    age: formatHours(support.pendingTriage.ageHoursAvg, t) ?? '—',
                })
                : null,
        },
        {
            key: 'created_today',
            icon: 'created',
            href: TRIAGE_LINK,
            count: support.createdToday.count,
            detail: t('dashboard.support_today_hint'),
        },
        {
            key: 'unanswered',
            icon: 'reply',
            href: TRIAGE_PAGE,
            count: support.unanswered.count,
            detail: t('dashboard.support_unanswered_hint', {
                hours: support.unanswered.thresholdHours,
            }),
        },
    ];

    const hasSignal = tiles.some((tile) => tile.count > 0);
    if (!hasSignal)
        return null;

    return (
        <div className="SupportIndicators rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <div className="flex items-end justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
                <h3 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white">
                    <Icon type="chart" size={6} className="mr-1.5" />
                    {t('dashboard.support_indicators')}
                </h3>
                <Link to={TRIAGE_PAGE} className="text-xs text-indigo-600 hover:underline">
                    {t('common.see_all')}
                    <Icon type="right" className="inline" />
                </Link>
            </div>
            <div className="grid grid-cols-1 gap-2 p-4 sm:grid-cols-3">
                {tiles.map((tile) => (
                    <Link
                        key={tile.key}
                        to={tile.href}
                        className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 transition hover:border-indigo-300 hover:bg-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700"
                    >
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                            <Icon type={tile.icon} size={4} />
                            {t(`dashboard.support_${tile.key}`)}
                        </div>
                        <div className="mt-0.5 text-2xl font-bold text-gray-900 dark:text-white">
                            {tile.count}
                        </div>
                        {tile.detail && (
                            <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                {tile.detail}
                            </div>
                        )}
                    </Link>
                ))}
            </div>
        </div>
    );
}

export default SupportIndicatorsWidget;
